import { useState, useEffect } from 'react'
import { Check, ArrowLeft, Tag, Loader } from 'lucide-react'
import PaymentForm from './PaymentForm'
import axios from 'axios'

const SquarePayment = ({ formData, membershipTypes, paymentMethods, onComplete, onBack }) => {
  const [paymentComplete, setPaymentComplete] = useState(false)
  const [confirmationNumber, setConfirmationNumber] = useState(null)
  const [paymentId, setPaymentId] = useState(null)
  const [discountCode, setDiscountCode] = useState('')
  const [appliedDiscount, setAppliedDiscount] = useState(null)
  const [discountError, setDiscountError] = useState('')
  const [validatingDiscount, setValidatingDiscount] = useState(false)

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }, [])

  // Scroll to top when payment completes
  useEffect(() => {
    if (paymentComplete) {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [paymentComplete])

  // Check if current date is after May 1st of current year
  const isAfterMay1 = () => {
    const today = new Date()
    const currentYear = today.getFullYear()
    const may1 = new Date(currentYear, 4, 1) // Month is 0-indexed, so 4 = May
    return today >= may1
  }

  // Calculate price breakdown
  const calculatePriceBreakdown = () => {
    const type = formData.membershipType === 'household' ? 'household' : 'singleSenior'
    const membershipInfo = membershipTypes[formData.memberStatus][type]

    const basePrice = membershipInfo.basePrice || 0
    const initiationFee = membershipInfo.initiationFee || 0
    const lateFee = isAfterMay1() ? 100 : 0

    const subtotal = basePrice + initiationFee + lateFee
    const discountAmount = appliedDiscount ? appliedDiscount.discountAmount : 0
    const total = Math.max(0, subtotal - discountAmount) // Don't go below 0

    return {
      basePrice,
      initiationFee,
      lateFee,
      subtotal,
      discountAmount,
      total
    }
  }

  // Validate and apply discount code
  const handleApplyDiscount = async () => {
    if (!discountCode.trim()) {
      setDiscountError('Please enter a discount code')
      return
    }

    setValidatingDiscount(true)
    setDiscountError('')

    try {
      const { subtotal } = calculatePriceBreakdown()
      const { data } = await axios.post('/api/discount-codes/validate', {
        code: discountCode.trim(),
        subtotal
      })

      setAppliedDiscount(data)
      setDiscountError('')
    } catch (error) {
      setDiscountError(error.response?.data?.message || 'Invalid discount code')
      setAppliedDiscount(null)
    } finally {
      setValidatingDiscount(false)
    }
  }

  // Remove applied discount
  const handleRemoveDiscount = () => {
    setAppliedDiscount(null)
    setDiscountCode('')
    setDiscountError('')
  }

  // Handle payment success
  const handlePaymentSuccess = async (payment) => {
    console.log('🎉 handlePaymentSuccess called with payment:', payment)

    try {
      // Apply discount code usage if one was used
      if (appliedDiscount) {
        console.log('📝 Applying discount code:', appliedDiscount.code)
        await axios.post('/api/discount-codes/apply', {
          code: appliedDiscount.code
        }).catch(err => {
          console.error('Error applying discount code:', err)
          // Don't fail the whole payment if discount tracking fails
        })
      }

      // Build registration data combining form data + payment info
      const registrationData = {
        ...formData,
        paymentMethod: payment.amounts.paymentMethod || 'card',
        paymentStatus: 'completed',
        paymentDate: new Date().toISOString(),
        paymentReference: payment.id,
        amountPaid: payment.amounts.total,
        discountCode: appliedDiscount ? appliedDiscount.code : null,
      }

      console.log('🔵 Creating member record with payment...', {
        name: `${formData.firstName} ${formData.lastName}`,
        paymentId: payment.id
      })

      // Create member record after successful payment
      const createResponse = await axios.post('/api/members/register-with-payment', registrationData)

      console.log('✅ Member creation response:', createResponse.data)

      setPaymentId(payment.id)
      setConfirmationNumber(`LFST-${Date.now()}`)
      setPaymentComplete(true)
    } catch (error) {
      console.error('❌ Error creating member record:', error)
      console.error('Error details:', error.response?.data || error.message)
      alert('Payment successful but error saving record. Please contact support with payment ID: ' + payment.id)
    }
  }

  // Payment complete screen
  if (paymentComplete) {
    return (
      <div className="bg-gray-50 min-h-screen py-8">
        <div className="container-custom max-w-3xl mx-auto">
          <div className="card p-8 text-center">
            <div className="mb-4">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Membership Confirmed!
              </h1>
              <p className="text-gray-600">
                Thank you for joining Lockridge Forest Swim & Tennis Club
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="text-xs text-gray-600 mb-1">Confirmation Number</div>
              <div className="text-xl font-bold text-blue-600 mb-2">{confirmationNumber}</div>
              <p className="text-xs text-gray-700">
                Please save this confirmation number for your records
              </p>
            </div>

            <div className="text-left mb-6">
              <h2 className="text-lg font-bold text-gray-900 mb-3">What Happens Next?</h2>
              <div className="space-y-2">
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-xs">
                    1
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-gray-900">Payment Confirmation</div>
                    <p className="text-gray-600 text-xs">
                      Payment processed successfully
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-xs">
                    2
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-gray-900">Email Confirmation</div>
                    <p className="text-gray-600 text-xs">
                      Confirmation details sent to {formData.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-start">
                  <div className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center mr-2 flex-shrink-0 text-xs">
                    3
                  </div>
                  <div>
                    <div className="font-semibold text-sm text-gray-900">Welcome Package</div>
                    <p className="text-gray-600 text-xs">
                      Membership cards and club info
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={onComplete}
              className="btn btn-primary w-full mb-3"
            >
              Return to Home
            </button>
            <p className="text-xs text-gray-600">
              Questions? Contact admin@lockridgeforestswimtennis.com or (770) 609-7428
            </p>
          </div>
        </div>
      </div>
    )
  }

  // Payment form screen
  const membershipType = formData.membershipType === 'household' ? 'Household' :
                          formData.membershipType === 'senior' ? 'Senior' : 'Single'
  const memberStatus = formData.memberStatus === 'new' ? 'New' : 'Returning'
  const description = `Lockridge Forest Swim Tennis Club - ${memberStatus} ${membershipType} Membership - ${formData.firstName} ${formData.lastName}`
  const pricing = calculatePriceBreakdown()

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="container-custom max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={onBack}
            className="flex items-center text-blue-600 hover:text-blue-800 mb-3 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to Application
          </button>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Complete Payment</h1>
          <p className="text-gray-600">
            Complete your membership payment securely
          </p>
        </div>

        {/* Application Summary */}
        <div className="card p-4 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-3">Application Summary</h2>
          <div className="grid md:grid-cols-2 gap-3 text-sm">
            <div>
              <span className="text-gray-600">Applicant:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {formData.firstName} {formData.lastName}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Email:</span>
              <span className="ml-2 font-semibold text-gray-900">{formData.email}</span>
            </div>
            <div>
              <span className="text-gray-600">Membership:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {membershipType}
              </span>
            </div>
            <div>
              <span className="text-gray-600">Status:</span>
              <span className="ml-2 font-semibold text-gray-900">
                {memberStatus} Member
              </span>
            </div>
          </div>
        </div>

        {/* Price Breakdown */}
        <div className="card p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4">Price Breakdown</h2>
          <div className="space-y-3">
            <div className="flex justify-between text-gray-700">
              <span>Annual Dues</span>
              <span className="font-semibold">${pricing.basePrice.toFixed(2)}</span>
            </div>

            {pricing.initiationFee > 0 && (
              <div className="flex justify-between text-gray-700">
                <span>Initiation Fee (One-time)</span>
                <span className="font-semibold">${pricing.initiationFee.toFixed(2)}</span>
              </div>
            )}

            {pricing.lateFee > 0 && (
              <div className="flex justify-between text-gray-700">
                <span>Late Joining Fee (After May 1st)</span>
                <span className="font-semibold">${pricing.lateFee.toFixed(2)}</span>
              </div>
            )}

            <div className="border-t pt-3 flex justify-between text-gray-900 font-semibold">
              <span>Subtotal</span>
              <span>${pricing.subtotal.toFixed(2)}</span>
            </div>

            {appliedDiscount && (
              <div className="flex justify-between text-green-600 font-semibold">
                <span className="flex items-center gap-2">
                  <Tag className="w-4 h-4" />
                  Discount ({appliedDiscount.code})
                </span>
                <span>-${pricing.discountAmount.toFixed(2)}</span>
              </div>
            )}

            <div className="border-t-2 border-gray-300 pt-3 flex justify-between text-xl font-bold text-gray-900">
              <span>Total</span>
              <span className="text-primary-600">${pricing.total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Discount Code Section */}
        <div className="card p-6 mb-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Tag className="w-5 h-5" />
            Discount Code
          </h2>

          {!appliedDiscount ? (
            <div>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={discountCode}
                  onChange={(e) => setDiscountCode(e.target.value.toUpperCase())}
                  onKeyPress={(e) => e.key === 'Enter' && handleApplyDiscount()}
                  placeholder="Enter discount code"
                  className="input flex-1"
                  disabled={validatingDiscount}
                />
                <button
                  onClick={handleApplyDiscount}
                  disabled={validatingDiscount || !discountCode.trim()}
                  className="btn btn-secondary flex items-center gap-2"
                >
                  {validatingDiscount ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Validating...
                    </>
                  ) : (
                    'Apply'
                  )}
                </button>
              </div>
              {discountError && (
                <p className="mt-2 text-sm text-red-600">{discountError}</p>
              )}
            </div>
          ) : (
            <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-lg p-4">
              <div>
                <div className="font-semibold text-green-900 flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  Discount Applied: {appliedDiscount.code}
                </div>
                {appliedDiscount.description && (
                  <p className="text-sm text-green-700 mt-1">{appliedDiscount.description}</p>
                )}
                <p className="text-sm text-green-700 mt-1">
                  You're saving ${pricing.discountAmount.toFixed(2)}
                </p>
              </div>
              <button
                onClick={handleRemoveDiscount}
                className="text-red-600 hover:text-red-800 text-sm font-semibold"
              >
                Remove
              </button>
            </div>
          )}
        </div>

        {/* Payment Form */}
        <PaymentForm
          amount={pricing.total}
          description={description}
          customerEmail={formData.email}
          customerName={`${formData.firstName} ${formData.lastName}`}
          referenceId={confirmationNumber}
          metadata={{
            membershipType: formData.membershipType,
            memberStatus: formData.memberStatus,
            discountCode: appliedDiscount?.code || null,
          }}
          onSuccess={handlePaymentSuccess}
          onCancel={onBack}
        />
      </div>
    </div>
  )
}

export default SquarePayment
